import React, { useEffect, useRef, useState, useCallback } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { Outlet, useLocation } from "react-router-dom";
import Loading from "./Loading";
import { useSelector, useDispatch } from "react-redux";
import { AppState, DEFAULT_APP_STATE } from "../../AppState/appState";
import { deploymentStatusApi } from "../../API/deploymentStatus";
import { PreviousPathContext } from "../../context/navigation/PreviousPathContext";
import { isTokenExpired, redirectToOktaLogin, getUserInfo } from "../../helpers";
import { validateOktaToken } from "../../API/validateOktaToken";
import { logUserIn, userPermissions } from "../../AppState/logUserIn";
import { ErrorMessages, STORAGE_KEYS } from "../../constants";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { PopoutNotification } from "../../AppState/popoutNotification";
import { get } from "lodash";

// Grace period after login callback to allow Okta SDK to complete token storage
const POST_CALLBACK_GRACE_PERIOD_MS = 3000;
// Max attempts to wait for Okta token storage
const MAX_TOKEN_WAIT_ATTEMPTS = 10;
// Delay between token wait attempts
const TOKEN_WAIT_DELAY_MS = 300;

export const RequiredAuth: React.FC = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: AppState) => state.isLoggedIn);
  const location = useLocation();
  const prevRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  // Track when we came from login callback to add grace period
  const postCallbackTimeRef = useRef<number | null>(null);
  // Prevent multiple redirect attempts
  const redirectAttemptedRef = useRef(false);

  /**
   * Check if Okta tokens are available in storage
   * The Okta SDK stores tokens asynchronously after callback processing
   */
  const areOktaTokensAvailable = useCallback(async (): Promise<boolean> => {
    try {
      // Use Okta SDK's getIdToken which reads from its token manager
      const idToken = await oktaAuth.tokenManager.get('idToken');
      return !!idToken;
    } catch {
      return false;
    }
  }, [oktaAuth]);

  /**
   * Wait for Okta tokens to be available with retry
   */
  const waitForOktaTokens = useCallback(async (): Promise<boolean> => {
    for (let i = 0; i < MAX_TOKEN_WAIT_ATTEMPTS; i++) {
      if (!isMountedRef.current) return false;
      
      const available = await areOktaTokensAvailable();
      if (available) {
        console.log(`Okta tokens available after ${i + 1} attempt(s)`);
        return true;
      }
      
      console.log(`Waiting for Okta tokens... attempt ${i + 1}/${MAX_TOKEN_WAIT_ATTEMPTS}`);
      await new Promise(resolve => setTimeout(resolve, TOKEN_WAIT_DELAY_MS));
    }
    return false;
  }, [areOktaTokensAvailable]);

  /**
   * Check if we're in the post-callback grace period
   */
  const isInPostCallbackGracePeriod = useCallback((): boolean => {
    if (!postCallbackTimeRef.current) return false;
    const elapsed = Date.now() - postCallbackTimeRef.current;
    return elapsed < POST_CALLBACK_GRACE_PERIOD_MS;
  }, []);

  /**
   * Handle error when tokens are not available
   */
  const handleTokensUnavailable = useCallback((): boolean => {
    if (isInPostCallbackGracePeriod()) {
      console.log("Still in post-callback grace period, will retry");
      setIsValidatingToken(false);
      return true; // Don't throw, will retry
    }
    return false;
  }, [isInPostCallbackGracePeriod]);

  /**
   * Handle validation errors
   */
  const handleValidationError = useCallback((err: any): void => {
    if (!isMountedRef.current) return;

    console.error("Token validation failed:", err);
    const message = err?.message ?? err;

    // Handle deployment errors silently (expected behavior)
    if (
      message === ErrorMessages.deploymentInprogressError ||
      message === ErrorMessages.statusCodeNotFound
    ) {
      console.warn("Deployment in progress or status check failed:", message);
      setIsValidatingToken(false);
      return;
    }

    // If within grace period after callback, don't redirect immediately
    if (isInPostCallbackGracePeriod() && !redirectAttemptedRef.current) {
      console.log("Token validation failed but within grace period, will retry");
      setIsValidatingToken(false);
      return;
    }

    // Prevent multiple redirect attempts
    if (redirectAttemptedRef.current) {
      console.warn("Redirect already attempted, skipping");
      setIsValidatingToken(false);
      return;
    }

    redirectAttemptedRef.current = true;

    // Show error notification for authentication failures
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        message ??
          `Login via Okta was successful, but authentication with the server failed. Please log out of Okta and log in again to retry`,
        Date.now().toString()
      )
    );

    // Redirect to login on failure
    redirectToOktaLogin(oktaAuth, "Token validation failed");
  }, [isInPostCallbackGracePeriod, dispatch, oktaAuth]);

  useEffect(() => {
    prevRef.current = location.pathname;
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Detect when we've just come from login callback
  useEffect(() => {
    // Check if previous path was login/callback
    if (prevRef.current?.includes('/login/callback')) {
      console.log('Detected navigation from login callback, starting grace period');
      postCallbackTimeRef.current = Date.now();
      redirectAttemptedRef.current = false;
    }
  }, [location.pathname]);

  // MAIN AUTH FLOW: Validate Okta token and fetch JWT after successful Okta login
  useEffect(() => {
    // Wait for authState to be ready
    if (!authState || authState.isPending) {
      return;
    }

    // Only proceed if user is authenticated in Okta
    if (!authState.isAuthenticated) {
      return;
    }

    // Skip if already logged in or currently validating
    if (isLoggedIn || isValidatingToken) {
      return;
    }

    // Check if JWT token already exists
    const existingJWT = getUserInfo()?.jwtToken;
    if (existingJWT) {
      // JWT exists, just set login state
      dispatch(logUserIn(true));
      return;
    }

    // Okta authenticated but no JWT - validate token to fetch JWT and permissions
    console.log("Okta authentication successful, validating token to fetch JWT...");
    setIsValidatingToken(true);

    // First, wait for Okta tokens to be available in storage
    // This prevents race condition after login callback
    const performValidation = async () => {
      try {
        // Wait for Okta tokens to be available
        const tokensAvailable = await waitForOktaTokens();
        
        if (!isMountedRef.current) return;
        
        if (!tokensAvailable) {
          console.warn("Okta tokens not available after waiting, checking grace period");
          // Check if we should retry during grace period
          if (handleTokensUnavailable()) return;
          throw new Error("Failed to retrieve Okta tokens after login");
        }

        const user = await validateOktaToken();
        
        if (!isMountedRef.current) return;

        console.log("Token validation successful, JWT and permissions fetched");
        localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
        
        // Set user permissions
        dispatch(
          userPermissions(
            get(user, "permissions", DEFAULT_APP_STATE.userPermissions)
          )
        );
        
        // Set logged in state - this will allow RequiredAuth to render protected routes
        dispatch(logUserIn(true));
        postCallbackTimeRef.current = null; // Clear grace period
        redirectAttemptedRef.current = false;
      } catch (err: any) {
        handleValidationError(err);
      } finally {
        if (isMountedRef.current) {
          setIsValidatingToken(false);
        }
      }
    };

    performValidation();
  }, [oktaAuth, authState, isLoggedIn, isValidatingToken, dispatch, waitForOktaTokens, handleTokensUnavailable, handleValidationError]);

  // Check JWT token expiration for already logged-in users
  useEffect(() => {
    // Wait for authState to be ready
    if (!authState || authState.isPending) {
      return;
    }

    // Only check token if user is authenticated in Okta AND logged in
    if (!authState.isAuthenticated || !isLoggedIn) {
      return;
    }

    try {
      const authUser = getUserInfo();
      const jwtToken = authUser?.jwtToken;
      
      // If no JWT token exists after login, this could be a timing issue
      if (!jwtToken) {
        // Don't redirect if we're still in post-callback grace period
        if (isInPostCallbackGracePeriod()) {
          console.log("JWT token missing but within grace period, waiting...");
          return;
        }
        
        // Prevent multiple redirects
        if (redirectAttemptedRef.current) {
          console.warn("JWT missing but redirect already attempted");
          return;
        }
        
        console.warn("JWT token missing despite being logged in, redirecting to login");
        redirectAttemptedRef.current = true;
        redirectToOktaLogin(oktaAuth, "JWT token missing");
        return;
      }

      // Check if token is expired or about to expire
      const tokenStatus = isTokenExpired(jwtToken, 2);
      
      // Redirect if token is expired (true) or expired yesterday ("expiredYesterday")
      if (tokenStatus === true) {
        console.warn("JWT token expired or expiring soon, redirecting to login");
        redirectToOktaLogin(oktaAuth, "Token expired or expiring");
      } else if (tokenStatus === "expiredYesterday") {
        console.warn("JWT token expired yesterday, redirecting to login");
        redirectToOktaLogin(oktaAuth, "Token expired yesterday");
      }
    } catch (error) {
      console.error("Token expiration check failed:", error);
      // Don't redirect on error to avoid loops - let interceptor handle it
    }
  }, [oktaAuth, authState, isLoggedIn, isInPostCallbackGracePeriod]);

  // Redirect if not authenticated in Okta
  useEffect(() => {
    // Wait for authState to be initialized
    if (!authState || authState.isPending) {
      return;
    }

    // If not authenticated in Okta, check if user has valid JWT before redirecting
    // This prevents unnecessary redirects when Okta token refresh is pending
    if (!authState.isAuthenticated) {
      // Check if user has a valid (non-expired) JWT - if so, don't redirect
      // This handles the case where Okta access token expired but JWT is still valid
      const existingJWT = getUserInfo()?.jwtToken;
      if (existingJWT) {
        const tokenStatus = isTokenExpired(existingJWT, 0);
        if (tokenStatus === false) {
          // JWT is still valid - don't redirect, let the main auth flow handle token refresh
          console.log("Okta not authenticated but valid JWT exists, skipping redirect");
          return;
        }
      }

      const checkDeploymentAndRedirect = async () => {
        try {
          const response = await deploymentStatusApi();
          if (response) {
            await redirectToOktaLogin(oktaAuth, "User not authenticated");
          }
        } catch (error) {
          // Log deployment check error but still proceed with authentication
          console.error("Deployment status check failed:", error);
          
          // Proceed with authentication even if deployment check fails
          try {
            await redirectToOktaLogin(oktaAuth, "User not authenticated (deployment check failed)");
          } catch (redirectError) {
            console.error("Error during authentication redirect:", redirectError);
          }
        }
      };

      checkDeploymentAndRedirect();
    }
  }, [oktaAuth, authState]);

  // Show loading if auth state is pending or not ready
  if (!authState || authState.isPending) {
    return <Loading />;
  }

  // Show loading if not authenticated, not logged in, or currently validating token
  if (!authState.isAuthenticated || !isLoggedIn || isValidatingToken) {
    return <Loading />;
  }

  // Render protected routes
  return (
    <PreviousPathContext.Provider value={prevRef.current}>
      <Outlet />
    </PreviousPathContext.Provider>
  );
};
